import React, { FC } from "react";
import styled from "styled-components";
import { FibiUsersBillingData, FibiUsersBillingType } from "./data";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { toast } from "react-toastify";
import useGetPrice from "../../../../hooks/useGetPrice";
import useOpener from "../../../../hooks/useOpener";
import { useFibiUsersCancelSubscription } from "../../../../hooks/utils/useUtils";
import TerminateModal from "../../components/TerminateModal";

interface Props {
  data: (typeof FibiUsersBillingData)[0];
  onButtonClick: (
    billingType: FibiUsersBillingType,
    price: number,
    paystackPlan: string
  ) => void;
  currentPlan?: string;
}

const FibiUsersBillingCard: FC<Props> = ({
  data,
  onButtonClick,
  currentPlan,
}) => {
  const isHighlighted = currentPlan ? data.id === currentPlan : false;
  const { isNigeria } = useGetPrice();
  const {
    open: unsubscribeOpen,
    onClose: unsubscribeOnClose,
    onOpen: unsubscribeOnOpen,
  } = useOpener();
  const { mutate, isLoading } = useFibiUsersCancelSubscription();

  const onClick = () => {
    if (data.id === FibiUsersBillingType.token) {
      onButtonClick(
        FibiUsersBillingType.token,
        data.price,
        data.paystackPlan as string
      );
    } else if (data.id === FibiUsersBillingType.full) {
      onButtonClick(
        FibiUsersBillingType.full,
        data.price,
        data.paystackPlan as string
      );
    }
  };

  const onUnsubscribe = () => {
    mutate(undefined, {
      onSuccess: () => {
        toast.success("Successful");
        unsubscribeOnClose();
      },
    });
  };

  return (
    <>
      <PricingCard
        className="relative transition-all hover:shadow-sm "
        isHighlighted={isHighlighted}
      >
        {isHighlighted && (
          <IoIosCloseCircleOutline
            className="absolute text-red-500 bg-white cursor-pointer -top-3 -right-3"
            size={30}
            onClick={unsubscribeOnOpen}
          />
        )}

        <PlanTitle>{data.title}</PlanTitle>
        <p className="mb-4 ">{data.subText}</p>

        <div className="flex flex-wrap items-center gap-2 my-2 mb-4 lg:gap-6">
          <p className={`flex items-baseline gap-1`}>
            <span className={`text-xl font-semibold tracking-tight`}>
              {isNigeria
                ? `₦${data.price.toLocaleString("en-US")}`
                : `$${data.dollarPrice.toLocaleString("en-US")}`}
            </span>
            <span className={`text-gray-500 text-base`}>{data.unit}</span>
          </p>
        </div>

        <FeatureList>
          {data.features.map((feature, i) => (
            <li key={i}>{feature}</li>
          ))}
        </FeatureList>

        <div className="flex flex-col w-full gap-2 mt-auto">
          <Button
            isHighlighted={isHighlighted}
            onClick={onClick}
            disabled={isHighlighted}
          >
            {isHighlighted ? "Current Plan" : data.buttonText}
          </Button>
        </div>
      </PricingCard>
      {unsubscribeOpen && (
        <TerminateModal
          title={`Cancel ${data.title} Subscription`}
          subText={`Are you sure you want to cancel your ${data.title.toLowerCase()} subscription?`}
          onClose={unsubscribeOnClose}
          onConfirm={onUnsubscribe}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default FibiUsersBillingCard;

const PricingCard = styled.div<{
  isHighlighted?: boolean;
}>`
  border: 1px solid ${(props) => (props.isHighlighted ? "#0073ff" : "#eeeeee")};
  border-radius: 12px;
  padding: 20px;
  width: 100%;
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
`;

const PlanTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 4px;
  font-weight: bold;
`;

const FeatureList = styled.ul`
  list-style: disc;
  padding: 0;
  margin-bottom: 20px;
  margin-left: 16px;

  &:last-child {
    list-style: none;
  }

  li {
    font-size: 16px;
    margin-bottom: 10px;
  }
`;

const Button = styled.button<{ isHighlighted?: boolean }>`
  background-color: ${(props) =>
    !props.isHighlighted ? "#0073ff" : "#f9fafb"};
  color: ${(props) => (props.isHighlighted ? "#6b7280" : "#fff")};
  border: 1px solid ${(props) => (!props.isHighlighted ? "#0073ff" : "#e5e7eb")};
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 16px;
  cursor: ${(props) => (props.isHighlighted ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    /* background-color: #f9fafb;
    border: none; */
  }
`;
